import { TERM_LABELS } from "./Common";

export const roundLimitedMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

export const formatLimitedMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `$${roundLimitedMoney(n).toFixed(2)}`;
};

export const parseLimitedTimeMs = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return n < 1e12 ? n * 1000 : n;
  }
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
};

/** Add calendar months to a timestamp (ms). */
export const addMonthsMs = (startMs, months) => {
  if (!Number.isFinite(startMs) || !Number.isFinite(months)) return null;
  const d = new Date(startMs);
  d.setMonth(d.getMonth() + months);
  return d.getTime();
};

/**
 * True when a Limited License for `termMonths` started at `startDate`
 * and has not yet ended (end = start + term months, or explicit endDate).
 */
export const isLimitedLicenseRunning = ({
  startDate,
  endDate = null,
  termMonths,
  nowMs = Date.now(),
} = {}) => {
  const term = Number(termMonths);
  if (!Number.isFinite(term) || term <= 0) return false;

  const startMs = parseLimitedTimeMs(startDate);
  if (startMs == null) return false;

  let endMs = parseLimitedTimeMs(endDate);
  if (endMs == null) {
    endMs = addMonthsMs(startMs, term);
  }
  if (endMs == null) return false;

  return nowMs < endMs;
};

export const applyLimitedDiscount = (price, discountPercent) => {
  const amt = Number(price);
  if (!Number.isFinite(amt)) return null;
  const pct = Number(discountPercent);
  if (!Number.isFinite(pct) || pct <= 0) return roundLimitedMoney(amt);
  return roundLimitedMoney(amt * (1 - Math.min(pct, 100) / 100));
};

/**
 * Unused remaining credit of an existing limited order.
 * payback = Amount * clamp((End - now) / (End - Start), 0, 1)
 * If times are missing but Amount exists, treat as fully remaining.
 */
export const computeLimitedPayback = (order, nowMs = Date.now()) => {
  if (!order) return 0;
  const amount = roundLimitedMoney(order.Amount);
  if (amount <= 0) return 0;

  const startMs = parseLimitedTimeMs(order.StartTime);
  const endMs = parseLimitedTimeMs(order.EndTime);

  if (startMs == null || endMs == null || endMs <= startMs) {
    return amount;
  }
  if (nowMs >= endMs) return 0;
  if (nowMs <= startMs) return amount;

  const fraction = (endMs - nowMs) / (endMs - startMs);
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const amountCents = Math.round(amount * 100);
  return Math.round(amountCents * clamped) / 100;
};

/**
 * Latest usable limited order for the current term on a device/site.
 * Prefers future EndTime; falls back to first row matching term.
 */
export const pickCurrentLimitedOrder = (orders, currentTerm) => {
  const list = Array.isArray(orders) ? orders : [];
  if (!list.length) return null;

  const term =
    currentTerm != null && currentTerm !== "" ? Number(currentTerm) : null;
  const now = Date.now();

  const withTerm =
    Number.isFinite(term) && term > 0
      ? list.filter((o) => Number(o?.Term) === term)
      : list;

  const pool = withTerm.length ? withTerm : list;
  const active = pool.find((o) => {
    const endMs = parseLimitedTimeMs(o?.EndTime);
    return endMs == null || endMs > now;
  });
  return active || pool[0] || null;
};

export const buildLimitedLicenseSummary = ({
  selectedCard,
  discountPercent = 0,
  currentTerm = null,
  currentCardBasePrice = null,
  currentPaidAmount = null,
  currentOrder = null,
  currentOrderStartDate = null,
  currentOrderEndDate = null,
}) => {
  const newPrice = Number(selectedCard?.Price);
  const pct = Number(discountPercent);
  const newAmount =
    Number.isFinite(newPrice) && newPrice > 0
      ? applyLimitedDiscount(newPrice, pct) ?? 0
      : 0;

  const termNum =
    currentTerm != null && currentTerm !== "" ? Number(currentTerm) : null;
  const newTermNum =
    selectedCard?.Term != null && selectedCard?.Term !== ""
      ? Number(selectedCard.Term)
      : null;
  const hasOld =
    (Number.isFinite(termNum) && termNum > 0) || currentOrder != null;

  let oldTerm = hasOld && Number.isFinite(termNum) ? termNum : null;
  let oldPrice = null;
  let oldAmount = null;

  const baseFromCard = Number(currentCardBasePrice);
  if (Number.isFinite(baseFromCard) && baseFromCard > 0) {
    oldPrice = baseFromCard;
    // Match Limited License modal Amount column (Price after dealer discount)
    oldAmount = applyLimitedDiscount(oldPrice, pct);
  }

  if (currentOrder) {
    oldTerm =
      currentOrder.Term != null && currentOrder.Term !== ""
        ? Number(currentOrder.Term)
        : oldTerm;

    if (oldPrice == null) {
      const op = Number(currentOrder.Price);
      if (Number.isFinite(op) && op > 0) {
        oldPrice = op;
        oldAmount = applyLimitedDiscount(oldPrice, pct);
      }
    }
  }

  // Fallbacks only when we don't have a limited-card list price
  if (oldAmount == null && currentPaidAmount != null) {
    const paid = Number(currentPaidAmount);
    if (Number.isFinite(paid) && paid > 0) oldAmount = paid;
  }

  if (oldAmount == null && oldPrice != null) {
    oldAmount = applyLimitedDiscount(oldPrice, pct);
  }

  const roundedOldAmount =
    oldAmount != null ? roundLimitedMoney(oldAmount) : null;

  const paybackOrder =
    hasOld && roundedOldAmount != null
      ? (() => {
          const start =
            currentOrder?.StartTime ??
            currentOrder?.StartDate ??
            currentOrderStartDate;
          let end =
            currentOrder?.EndTime ??
            currentOrder?.EndDate ??
            currentOrderEndDate;
          if (end == null && start != null && Number.isFinite(oldTerm)) {
            const startMs = parseLimitedTimeMs(start);
            const computedEnd =
              startMs != null ? addMonthsMs(startMs, oldTerm) : null;
            if (computedEnd != null) end = computedEnd;
          }
          return {
            Amount: roundedOldAmount,
            StartTime: start,
            EndTime: end,
          };
        })()
      : null;

  const isDowngrade =
    Number.isFinite(oldTerm) &&
    Number.isFinite(newTermNum) &&
    newTermNum < oldTerm;

  // Upgrade: payback matches Old Amount shown in the summary.
  // Downgrade: unused credit is prorated by remaining term.
  const payback =
    paybackOrder == null
      ? 0
      : isDowngrade
        ? computeLimitedPayback(paybackOrder)
        : roundedOldAmount;

  const newAmountCents = Math.round(newAmount * 100);
  const paybackCents = Math.round(payback * 100);
  // Downgrade: no charge, but still show unused credit as payback
  const amountDue = isDowngrade
    ? 0
    : Math.max(newAmountCents - paybackCents, 0) / 100;

  return {
    old: hasOld
      ? {
          term: oldTerm,
          termLabel:
            TERM_LABELS[oldTerm] || (oldTerm != null ? String(oldTerm) : "-"),
          price: oldPrice,
          amount: roundedOldAmount,
        }
      : null,
    next: {
      limitedCardNo: selectedCard?.LimitedCardNo,
      term: selectedCard?.Term,
      termLabel:
        TERM_LABELS[selectedCard?.Term] ||
        (selectedCard?.Term != null ? String(selectedCard.Term) : "-"),
      name: selectedCard?.LimitedCardName || "",
      price: Number.isFinite(newPrice) ? newPrice : null,
      amount: newAmount,
    },
    payback,
    amountDue,
    isDowngrade,
    dealerDiscountPercent:
      Number.isFinite(pct) && pct > 0 ? Math.min(pct, 100) : 0,
  };
};
